import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import './StampCustomForm.css'; // mismo estilo que StampCustomForm
import {
  UY_DEPARTMENTS,
  getCitiesForDepartment,
  findDepartmentMatch,
  findCityMatch,
} from '../data/uruguayLocations';

const OTHER_CITY = '__OTHER__';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const URUGUAY_BOUNDS = [
  [-34.98, -58.45],
  [-30.08, -53.21],
];
const URUGUAY_CENTER = [-32.8, -56.0];
const DEPARTMENT_ZOOM = 9;

const DEPARTMENT_CENTERS = {
  artigas: [-30.4, -56.47],
  canelones: [-34.52, -55.9],
  'cerro largo': [-32.35, -54.17],
  colonia: [-34.25, -57.7],
  durazno: [-33.37, -56.52],
  flores: [-33.68, -56.88],
  florida: [-34.1, -56.22],
  lavalleja: [-34.13, -55.05],
  maldonado: [-34.6, -54.95],
  montevideo: [-34.9011, -56.1645],
  paysandu: [-32.32, -58.08],
  'rio negro': [-32.85, -57.7],
  rivera: [-30.9, -55.55],
  rocha: [-34.48, -54.33],
  salto: [-31.4, -57.83],
  'san jose': [-34.34, -56.72],
  soriano: [-33.4, -58.1],
  tacuarembo: [-31.72, -55.98],
  'treinta y tres': [-33.23, -54.38],
};

const normalizeDepartmentName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

let leafletLoader;

function loadLeaflet() {
  if (window.L) {
    return Promise.resolve(window.L);
  }

  if (leafletLoader) {
    return leafletLoader;
  }

  leafletLoader = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Leaflet failed to load'));
    document.body.appendChild(script);
  });

  return leafletLoader;
}

function parseMapAddress(data) {
  const displayName = data?.display_name || '';
  const parts = displayName
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const addressData = data?.address || {};
  const street = [addressData.road, addressData.house_number]
    .filter(Boolean)
    .join(' ');
  const city =
    addressData.city ||
    addressData.town ||
    addressData.village ||
    addressData.municipality ||
    addressData.county ||
    '';
  const department =
    addressData.state || addressData.region || addressData.state_district || '';
  const postalCode = addressData.postcode || '';

  if (street && city && department && postalCode) {
    return { address: street, city, department, postalCode };
  }

  const nonCountryParts = parts.filter(
    (part) => part.toLowerCase() !== 'uruguay',
  );
  const fallbackDepartment =
    department || nonCountryParts[nonCountryParts.length - 1] || '';
  const fallbackCity =
    city || nonCountryParts[nonCountryParts.length - 2] || '';
  const streetParts = nonCountryParts.slice(
    0,
    Math.max(1, nonCountryParts.length - 2),
  );
  const fallbackAddress = street || streetParts.join(', ');

  return {
    address: fallbackAddress,
    city: fallbackCity,
    department: fallbackDepartment,
    postalCode,
  };
}

// Ajusta el departamento/ciudad geocodeados a las opciones válidas de los
// dropdowns. La ciudad geocodeada puede no estar en nuestra lista curada:
// en ese caso queda en modo "otra ciudad" con el texto tal cual se resolvió,
// en vez de perderlo silenciosamente.
function resolveDeptCity(rawDepartment, rawCity) {
  const department = findDepartmentMatch(rawDepartment) || '';
  const matchedCity = department ? findCityMatch(rawCity, department) : null;

  return {
    department,
    city: matchedCity || rawCity || '',
    isCustomCity: Boolean(rawCity) && !matchedCity,
  };
}

function CustomerForm({ customer, onChange }) {
  const [form, setForm] = useState({
    name: customer.name || '',
    address: customer.address || '',
    city: customer.city || '',
    department: customer.department || '',
    postalCode: customer.postalCode || '',
    phone: customer.phone || '',
    email: customer.email || '',
  });
  const [isCustomCity, setIsCustomCity] = useState(() => {
    if (!customer.city) return false;
    return !findCityMatch(customer.city, customer.department);
  });
  const [addressValid, setAddressValid] = useState(Boolean(customer.address));
  const [addressMessage, setAddressMessage] = useState('');
  const [showMap, setShowMap] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const markerIconRef = useRef(null);
  const departmentRef = useRef(form.department);
  departmentRef.current = form.department;

  useEffect(() => {
    // Validar campos sencillos. La dirección ya no depende de haberla
    // validado en el mapa (eso es solo una ayuda opcional), y el código
    // postal no es obligatorio porque muchas direcciones de Uruguay no
    // lo tienen cargado en OpenStreetMap.
    const isValid =
      form.name.trim() !== '' &&
      form.address.trim() !== '' &&
      form.city.trim() !== '' &&
      form.department.trim() !== '' &&
      form.phone.trim() !== '' &&
      /^\S+@\S+\.\S+$/.test(form.email.trim());

    onChange({ ...form, isValid });
  }, [form, onChange]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDepartmentChange = (e) => {
    const { value } = e.target;
    setIsCustomCity(false);
    setForm((prev) => ({ ...prev, department: value, city: '', postalCode: '' }));
  };

  const handleCityDropdownChange = (e) => {
    const { value } = e.target;
    if (value === OTHER_CITY) {
      setIsCustomCity(true);
      setForm((prev) => ({ ...prev, city: '', postalCode: '' }));
      return;
    }

    setIsCustomCity(false);
    setForm((prev) => ({ ...prev, city: value, postalCode: '' }));
  };

  const handleAddressBlur = useCallback(async () => {
    const address = form.address.trim();
    if (!address || (form.city.trim() && form.department.trim())) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=1&countrycodes=uy&accept-language=es&q=${encodeURIComponent(
          `${address}, Uruguay`,
        )}`,
      );

      if (!response.ok) return;

      const results = await response.json();
      const best = results?.[0];
      if (!best) return;

      const parsed = parseMapAddress(best);
      const resolved = resolveDeptCity(parsed.department, parsed.city);

      if (!form.city.trim() && !form.department.trim() && resolved.isCustomCity) {
        setIsCustomCity(true);
      }

      setForm((prev) => ({
        ...prev,
        city: prev.city.trim() ? prev.city : resolved.city,
        department: prev.department.trim() ? prev.department : resolved.department,
        postalCode: prev.postalCode.trim() ? prev.postalCode : parsed.postalCode,
      }));
    } catch (error) {
      // Autocompletado best-effort: si falla, el usuario completa a mano.
    }
  }, [form.address, form.city, form.department]);

  const handleMapSelect = useCallback(async (latlng) => {
    setAddressValid(false);
    setAddressMessage('Validando dirección...');
    setIsCustomCity(false);
    setForm((prev) => ({
      ...prev,
      address: '',
      city: '',
      department: '',
      postalCode: '',
    }));

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latlng.lat}&lon=${latlng.lng}&accept-language=es`,
      );

      if (!response.ok) {
        throw new Error('Response not ok');
      }

      const data = await response.json();
      const isUruguay = data?.address?.country_code === 'uy';

      if (!isUruguay) {
        setForm((prev) => ({
          ...prev,
          address: '',
          city: '',
          department: '',
          postalCode: '',
        }));
        setAddressValid(false);
        setAddressMessage('Seleccioná una ubicación dentro de Uruguay.');
        return;
      }

      const parsed = parseMapAddress(data);
      const resolved = resolveDeptCity(parsed.department, parsed.city);
      setIsCustomCity(resolved.isCustomCity);
      setForm((prev) => ({
        ...prev,
        address: parsed.address,
        city: resolved.city,
        department: resolved.department,
        postalCode: parsed.postalCode,
      }));
      setAddressValid(true);
      setAddressMessage('Dirección validada en Uruguay.');
    } catch (error) {
      setAddressValid(false);
      setAddressMessage('No se pudo validar la dirección. Intentá nuevamente.');
    }
  }, []);

  useEffect(() => {
    if (!showMap) {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markerRef.current = null;
      return undefined;
    }

    let mounted = true;

    loadLeaflet()
      .then((L) => {
        if (!mounted || !mapContainerRef.current || mapInstanceRef.current) {
          return;
        }

        const departmentCenter = DEPARTMENT_CENTERS[normalizeDepartmentName(departmentRef.current)];

        const map = L.map(mapContainerRef.current, {
          center: departmentCenter || URUGUAY_CENTER,
          zoom: departmentCenter ? DEPARTMENT_ZOOM : 6,
          maxBounds: URUGUAY_BOUNDS,
          maxBoundsViscosity: 0.8,
          scrollWheelZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        markerIconRef.current = L.icon({
          iconRetinaUrl:
            'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl:
            'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl:
            'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41],
        });

        map.on('click', (event) => {
          handleMapSelect(event.latlng);
          if (!markerRef.current) {
            markerRef.current = L.marker(event.latlng, {
              icon: markerIconRef.current || undefined,
            }).addTo(map);
          } else {
            markerRef.current.setLatLng(event.latlng);
          }
        });

        mapInstanceRef.current = map;
        setTimeout(() => {
          map.invalidateSize();
        }, 0);
      })
      .catch(() => {
        if (mounted) {
          setAddressMessage('No se pudo cargar el mapa.');
        }
      });

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markerRef.current = null;
    };
  }, [handleMapSelect, showMap]);

  return (
    <form className="poster-form" onSubmit={(e) => e.preventDefault()}>
      <label htmlFor="customerName">Nombre completo</label>
      <input
        id="customerName"
        name="name"
        type="text"
        value={form.name}
        onChange={handleInputChange}
        required
      />

      <label htmlFor="address">Dirección</label>
      <input
        id="address"
        name="address"
        type="text"
        value={form.address}
        onChange={handleInputChange}
        onBlur={handleAddressBlur}
        required
      />

      <label htmlFor="department">Departamento</label>
      <select
        id="department"
        name="department"
        value={form.department}
        onChange={handleDepartmentChange}
        required
      >
        <option value="" disabled>
          Selecciona un departamento
        </option>
        {UY_DEPARTMENTS.map((dept) => (
          <option key={dept} value={dept}>
            {dept}
          </option>
        ))}
      </select>

      <label htmlFor="city">Ciudad</label>
      <select
        id="city"
        value={isCustomCity ? OTHER_CITY : form.city}
        onChange={handleCityDropdownChange}
        disabled={!form.department}
        required
      >
        <option value="" disabled>
          {form.department ? 'Selecciona una ciudad' : 'Elegí primero un departamento'}
        </option>
        {getCitiesForDepartment(form.department).map((city) => (
          <option key={city} value={city}>
            {city}
          </option>
        ))}
        <option value={OTHER_CITY}>Otra ciudad</option>
      </select>
      {isCustomCity && (
        <input
          id="cityOther"
          name="city"
          type="text"
          placeholder="Escribí tu ciudad"
          value={form.city}
          onChange={handleInputChange}
          required
        />
      )}

      <label htmlFor="postalCode">Código postal (opcional)</label>
      <input
        id="postalCode"
        name="postalCode"
        type="text"
        value={form.postalCode}
        onChange={handleInputChange}
      />
      <a
        href="#mapa-direccion"
        className="map-toggle"
        onClick={(event) => {
          event.preventDefault();
          setShowMap((prev) => !prev);
        }}
      >
        {showMap ? 'Cerrar mapa' : 'Seleccionar en el mapa'}
      </a>
      {showMap && (
        <div className="map-helper">
          Seleccioná tu dirección en el mapa (solo Uruguay).
        </div>
      )}
      {showMap && (
        <div className="map-container" aria-live="polite" id="mapa-direccion">
          <div className="leaflet-map" ref={mapContainerRef} />
        </div>
      )}
      <div
        className={addressValid ? 'map-message' : 'map-error'}
        aria-live="polite"
      >
        {addressMessage}
      </div>

      <label htmlFor="phone">Teléfono</label>
      <input
        id="phone"
        name="phone"
        type="tel"
        value={form.phone}
        onChange={handleInputChange}
        required
      />

      <label htmlFor="email">Email</label>
      <input
        id="email"
        name="email"
        type="email"
        value={form.email}
        onChange={handleInputChange}
        required
      />
    </form>
  );
}

CustomerForm.propTypes = {
  customer: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    department: PropTypes.string,
    postalCode: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    isValid: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CustomerForm;
